'use client';

import React, { useEffect, useRef, useState } from 'react';
import { gridMetrics } from './hero/HeroGrid';
import { routePath } from '@/lib/routes';
import {
  Link2,
  QrCode,
  Route,
  Braces,
  Sparkles,
  Megaphone,
  Code2,
  Globe,
  BarChart3,
  Blocks,
  Building2,
  ArrowRight,
  ArrowUpRight,
  ChevronDown,
} from 'lucide-react';

type Item = {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  title: string;
  desc: string;
  href: string;
  badge?: string;
  sublinks?: { label: string; href: string }[];
};

const CORE: Item[] = [
  { icon: Link2, title: 'Link Management', desc: 'Paths, redirect codes, expiry & edits', href: routePath('linkManagement') },
  { icon: QrCode, title: 'QR Codes', desc: 'Dynamic QR codes for any link', href: routePath('qrCodes'), badge: 'New' },
  { icon: Route, title: 'Smart Routing', desc: 'Redirect by geo, device & time', href: routePath('smartRouting'), badge: 'New' },
  {
    icon: Braces,
    title: 'Dynamic Responses',
    desc: 'Return 30x redirects or raw JSON',
    href: routePath('dynamicResponses'),
    sublinks: [
      { label: 'Redirect', href: routePath('dynamicResponses') },
      { label: 'JSON', href: routePath('dynamicResponses') },
    ],
  },
];

const PREVIEW: Item[] = [
  { icon: Sparkles, title: 'AI & MCP', desc: 'Let AI agents manage your links', href: routePath('aiMcp') },
];

const USE_CASES: Item[] = [
  { icon: Megaphone, title: 'Marketing campaigns', desc: 'Track & route campaign traffic', href: routePath('solutionsMarketing') },
  { icon: Building2, title: 'Enterprise', desc: 'SSO / SAML on request', href: routePath('solutionsEnterprise') },
  { icon: Sparkles, title: 'AI & LLM apps', desc: 'Serve structured data to agents', href: routePath('aiMcp') },
  { icon: Code2, title: 'REST API & MCP', desc: 'Automate links programmatically', href: routePath('developers') },
  { icon: Globe, title: 'Custom domains', desc: 'Bring your own branded domain', href: routePath('customDomains') },
  { icon: BarChart3, title: 'Analytics', desc: 'Live click & routing insights', href: routePath('analytics') },
  { icon: Blocks, title: 'Integrations', desc: 'REST API, MCP & live click stream', href: routePath('integrations') },
];

const ItemRow: React.FC<{ item: Item; onNavigate: () => void }> = ({ item, onNavigate }) => {
  const Icon = item.icon;
  return (
    <a
      href={item.href}
      onClick={onNavigate}
      className="group flex gap-3 rounded-lg p-3 transition-colors hover:bg-slate-50"
    >
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-500 transition-colors group-hover:bg-brand-50 group-hover:text-brand-600">
        <Icon size={16} />
      </span>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-900">{item.title}</span>
          {item.badge && (
            <span className="rounded-full bg-brand-100 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-brand-600">
              {item.badge}
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs leading-snug text-slate-500">{item.desc}</p>
        {item.sublinks && (
          <div className="mt-2 flex gap-2">
            {item.sublinks.map((s) => (
              <span
                key={s.label}
                className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-600 transition-colors group-hover:border-slate-300"
              >
                {s.label}
                <ArrowUpRight size={11} className="text-slate-400" />
              </span>
            ))}
          </div>
        )}
      </div>
    </a>
  );
};

const ColHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">{children}</p>
);

const ONE_CELL = 'var(--grid-cell, 72px)';

const ProductsMenu: React.FC<{ dark?: boolean }> = ({ dark = false }) => {
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // The flyout uses the navbar's content band as the general content width: it
  // spans exactly the grid container and splits it into three whole-cell columns,
  // so the dividers land on grid lines and the content lines up with the navbar
  // (first item under the logo, customer-story column ending under the buttons).
  const [box, setBox] = useState({ left: 108, width: 1224, template: 'repeat(3, 1fr)' });

  useEffect(() => {
    const update = () => {
      const w = document.documentElement.clientWidth;
      const { cell, snapped, offsetX } = gridMetrics(w);
      const n = Math.max(3, Math.round(snapped / cell));
      const c1 = Math.ceil(n / 3);
      const c2 = Math.floor((n - c1) / 2);
      const c3 = n - c1 - c2;
      setBox({
        left: offsetX,
        width: snapped,
        template: [c1, c2, c3].map((c) => `${c * cell}px`).join(' '),
      });
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const openMenu = () => {
    clearTimeout(timer.current);
    setOpen(true);
  };
  const closeMenu = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(false), 120);
  };

  // Grid lines drawn on the (white) bar; the white content band masks them so the
  // grid shows only in the side areas, never behind the content.
  const gridOverlay: React.CSSProperties = {
    backgroundImage:
      'linear-gradient(to right,  rgba(15,23,42,0.07) 1px, transparent 1px),' +
      'linear-gradient(to bottom, rgba(15,23,42,0.07) 1px, transparent 1px)',
    backgroundSize: `${ONE_CELL} ${ONE_CELL}`,
    backgroundPosition: `${box.left}px 0`,
  };

  return (
    <>
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onMouseEnter={openMenu}
        onMouseLeave={closeMenu}
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-sm font-medium transition-colors ${
          open
            ? dark
              ? 'bg-white/10 text-white'
              : 'bg-slate-100 text-slate-900'
            : dark
              ? 'text-white/80 hover:text-white'
              : 'text-slate-600 hover:text-brand-600'
        }`}
      >
        Products
        <ChevronDown size={14} className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Full-width (100vw covers the scrollbar gutter) WHITE bar dropped right
          under the one-row navbar. Grid lines are drawn on the white sides; the
          white content band (sized to the navbar's content band, whole-cell
          columns so the dividers fall on grid lines) masks the grid behind it. */}
      <div
        onMouseEnter={openMenu}
        onMouseLeave={closeMenu}
        style={{ top: ONE_CELL, width: '100vw', ...gridOverlay }}
        className={`grid-gutter fixed left-0 z-40 border-b border-slate-200/70 bg-white shadow-[0_28px_48px_-28px_rgba(15,23,42,0.25)] transition-all duration-200 ${
          open ? 'visible translate-y-0 opacity-100' : 'invisible -translate-y-1 opacity-0'
        }`}
      >
        <div
          className="grid-edge grid bg-white"
          style={{
            marginLeft: box.left,
            width: box.width,
            gridTemplateColumns: box.template,
            // Redraw the grid lines the band masks at its LEFT and TOP edges
            // (the right edge's line falls just outside and stays visible). The
            // top line is the navbar↔flyout divider in the content area, which
            // the navbar itself doesn't draw when unscrolled.
            boxShadow: 'inset 1px 0 0 0 rgba(15,23,42,0.07), inset 0 1px 0 0 rgba(15,23,42,0.07)',
          }}
        >
          {/* Column 1 — Core */}
          <div className="py-7 px-5">
            <ColHeader>Core</ColHeader>
            <div className="space-y-0.5">
              {CORE.map((it) => (
                <ItemRow key={it.title} item={it} onNavigate={() => setOpen(false)} />
              ))}
            </div>
            <div className="mt-4 border-t border-slate-100 pt-4">
              <ColHeader>Preview</ColHeader>
              {PREVIEW.map((it) => (
                <ItemRow key={it.title} item={it} onNavigate={() => setOpen(false)} />
              ))}
            </div>
          </div>

          {/* Column 2 — Use cases */}
          <div className="border-l border-slate-200/60 py-7 px-5">
            <ColHeader>Use Cases</ColHeader>
            <div className="space-y-0.5">
              {USE_CASES.map((it) => (
                <ItemRow key={it.title} item={it} onNavigate={() => setOpen(false)} />
              ))}
              <a
                href={`${routePath('home')}#ref-features`}
                onClick={() => setOpen(false)}
                className="group flex items-center gap-3 rounded-lg p-3 transition-colors hover:bg-slate-50"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-500">
                  <ArrowRight size={16} />
                </span>
                <div>
                  <span className="text-sm font-semibold text-slate-900">View more</span>
                  <p className="mt-0.5 text-xs text-slate-500">Explore all use cases</p>
                </div>
              </a>
            </div>
          </div>

          {/* Column 3 — featured guide from The Redirect. (Was a "Customer story"
              teaser — "How Acme scaled to 2M links" — with dead "#" links and
              no such story; replaced in the claims sweep, 2026-09-27.) */}
          <div className="flex flex-col border-l border-slate-200/60 py-7 pl-5 pr-8">
            <ColHeader>From the journal</ColHeader>
            <a
              href={`${routePath('learn')}/migrating-50k-links`}
              onClick={() => setOpen(false)}
              className="group relative mt-2 flex flex-1 flex-col rounded-xl border border-slate-200/70 bg-slate-50/60 p-5 transition-colors hover:border-brand-200 hover:bg-brand-50/40"
            >
              <ArrowUpRight
                size={18}
                className="absolute right-4 top-4 text-slate-300 transition-colors group-hover:text-brand-500"
              />
              <span className="w-fit rounded-full bg-brand-100 px-2 py-0.5 text-[11px] font-semibold text-brand-600">
                Guide
              </span>
              {/* Abstract rii mark */}
              <div className="mt-5 grid w-7 grid-cols-2 gap-1">
                <span className="h-3 w-3 rounded-[3px] bg-slate-900" />
                <span className="h-3 w-3 rounded-[3px] bg-brand-500" />
                <span className="h-3 w-3 rounded-[3px] bg-brand-500" />
                <span className="h-3 w-3 rounded-[3px] bg-slate-900" />
              </div>
              <h4 className="mt-4 text-lg font-bold leading-tight text-slate-900">
                How to migrate <span className="text-brand-600">50,000 links</span> without losing a click
              </h4>
            </a>
            <a
              href={routePath('learn')}
              onClick={() => setOpen(false)}
              className="group mt-4 flex items-center gap-3 border-t border-slate-100 pt-4"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-500">
                <ArrowRight size={16} />
              </span>
              <div>
                <span className="text-sm font-semibold text-slate-900 group-hover:text-brand-600">The Redirect</span>
                <p className="mt-0.5 text-xs text-slate-500">Guides and playbooks on links and routing</p>
              </div>
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductsMenu;
